import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, Menu } from 'antd';
import { LogoutOutlined, DownOutlined } from '@ant-design/icons';

import { useAuth } from '../../contexts/AuthContext';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import '../../styles/PortalProfessorLayout.css';

// Shell de navegação do Portal do Professor (Fase 14A) -- cabeçalho,
// menu e logout. Não contém regra de negócio de nenhum módulo: cada
// item do menu (exceto Início/Sair, sempre disponíveis para quem já
// autenticou como PROFESSOR) só aparece se o RBAC real do usuário
// autorizar `visualizar` no recurso correspondente -- nunca por
// `user.role`. "Pagamentos" é só o rótulo de apresentação; a
// autorização por trás dele é o recurso real `folha_pagamento` (não
// existe recurso `pagamentos` no RBAC, e não deve ser criado só para
// isto).
const ITENS_MENU = [
  { label: 'Aulas Dadas', path: '/professor/aulas-dadas', recurso: 'aulas_dadas' },
  { label: 'Turmas', path: '/professor/turmas', recurso: 'turmas' },
  { label: 'Avaliações', path: '/professor/avaliacoes', recurso: 'avaliacoes' },
  { label: 'Pagamentos', path: '/professor/pagamentos', recurso: 'folha_pagamento' },
];

const PortalProfessorLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Hooks não podem ser chamados condicionalmente/em loop -- uma chamada
  // fixa por recurso do menu (só 4), cada uma decidindo sua própria
  // visibilidade via `podeVisualizar` (RBAC real do usuário).
  const permissaoAulasDadas = usePermissaoRecurso('aulas_dadas');
  const permissaoTurmas = usePermissaoRecurso('turmas');
  const permissaoAvaliacoes = usePermissaoRecurso('avaliacoes');
  const permissaoFolhaPagamento = usePermissaoRecurso('folha_pagamento');

  const permissaoPorRecurso = {
    aulas_dadas: permissaoAulasDadas,
    turmas: permissaoTurmas,
    avaliacoes: permissaoAvaliacoes,
    folha_pagamento: permissaoFolhaPagamento,
  };

  const itensVisiveis = ITENS_MENU.filter((item) => permissaoPorRecurso[item.recurso]?.podeVisualizar);

  const nomeExibicao = user?.nome_completo || user?.email || 'Professor';
  const iniciais = nomeExibicao.charAt(0).toUpperCase();

  const perfilMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout} danger>
        Sair
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="portal-professor-layout">
      <header className="portal-professor-layout__header">
        <div className="portal-professor-layout__brand">
          <span className="portal-professor-layout__brand-name">Fisicore</span>
          <span className="portal-professor-layout__brand-sub">Portal do Professor</span>
        </div>

        <nav className="portal-professor-layout__nav" aria-label="Navegação do portal">
          <Link
            to="/professor"
            className={`portal-professor-layout__nav-link${
              location.pathname === '/professor' ? ' portal-professor-layout__nav-link--ativo' : ''
            }`}
          >
            Presenças
          </Link>
          {itensVisiveis.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`portal-professor-layout__nav-link${
                location.pathname === item.path ? ' portal-professor-layout__nav-link--ativo' : ''
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <Dropdown overlay={perfilMenu} trigger={['click']} placement="bottomRight">
          <div className="portal-professor-layout__perfil" role="button" tabIndex={0}>
            <div className="portal-professor-layout__avatar">{iniciais}</div>
            <span className="portal-professor-layout__nome">{nomeExibicao}</span>
            <DownOutlined style={{ fontSize: 11 }} />
          </div>
        </Dropdown>
      </header>

      <main className="portal-professor-layout__content">{children}</main>
    </div>
  );
};

export default PortalProfessorLayout;
